import React, {useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'

import {
    Box,
    Flex,
    Heading,
    Image,
    Text,
    useMediaQuery
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {colors} from '../../constants'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {options} from '../rich-text/Options'
import './category-card.scss'

const CategoryCard = ({data}) => {
    const [isDesktop] = useMediaQuery('(min-width: 992px)')
    const color = data?.theme?.color ?? colors.black
    const backgroundColor = useMemo(() => {
        const backgroundThemeColor = data?.theme?.backgroundColor
        if (backgroundThemeColor === 'transparent') return {}
        return {
            backgroundColor: backgroundThemeColor,
            p: '10px'
        }
    }, [data])

    const image = useMemo(() => {
        const assets = data?.banner
        if (!assets) return
        return !isDesktop && assets?.mobile?.url
            ? assets?.mobile
            : assets?.desktop
    }, [data])

    const cta = data?.cta
    const goToCta = useCallback(() => {
        if (cta) location.href = cta?.url ?? '#'
    }, [])

    return (
        <Flex
            flex="1"
            gap="16px"
            borderRadius="24px"
            flexDirection="column"
            style={{cursor: cta ? 'pointer' : 'auto'}}
            onClick={goToCta}
            {...backgroundColor}
        >
            {image && (
                <Image
                    src={image?.url}
                    alt={`Newsletter image - ${data?.name}`}
                    style={{
                        borderRadius: '24px',
                        width: {base: '100%', md: 'auto', lg: 'auto'},
                        height: {base: '100%', md: 'auto', lg: 'auto'},
                        maxHeight: '547px',
                        objectFit: 'cover'
                    }}
                />
            )}
            <Flex
                flexDirection="column"
                gap={{base: '8px', lg: '6px', xl: '8px'}}
            >
                <Heading
                    as="h2"
                    color={color}
                    fontSize="24px"
                    fontFamily="Raleway"
                    fontWeight="500"
                    lineHeight="32px"
                >
                    {data?.name}
                </Heading>
                {data?.subTitle && (
                    <Text
                        fontSize="14px"
                        fontFamily="Raleway"
                        fontWeight="300"
                        color={color}
                    >
                        {data?.subTitle}
                    </Text>
                )}
                {data?.description && (
                    <Box className="category-card" color={color}>
                        {documentToReactComponents(
                            data?.description,
                            options({
                                entries: {inline: []},
                                assets: {block: []}
                            })
                        )}
                    </Box>
                )}
            </Flex>
        </Flex>
    )
}

CategoryCard.displayName = 'CategoryCard'

CategoryCard.propTypes = {
    data: PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.object,
        subTitle: PropTypes.string,
        cta: PropTypes.shape({
            text: PropTypes.string,
            url: PropTypes.string,
            externalUrl: PropTypes.string,
            openLinkInNewTab: PropTypes.bool,
            theme: PropTypes.string,
            type: PropTypes.string,
            isExternal: PropTypes.bool
        }),
        title: PropTypes.string,
        banner: PropTypes.shape({
            desktop: PropTypes.shape({
                url: PropTypes.string
            }),
            position: PropTypes.string
        }),
        theme: PropTypes.shape({
            backgroundColor: PropTypes.string,
            color: PropTypes.string
        })
    })
}

export default CategoryCard
